// Package imports:
import React, { useRef, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import * as XLSX from 'xlsx'
import ExcelJS from 'exceljs';
// Style imports:
import './TickDataRange.scss';
// Component imports:
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faChevronDown, faChevronRight, faCircle } from '@fortawesome/pro-solid-svg-icons';
import StickyHeaderTable from '../common/StickyHeaderTable';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';
import Table from 'react-bootstrap/Table';
import SortFilterTable, { IColumn } from '../common/SortFilterTable';
import Button from 'react-bootstrap/Button';
// Service imports:
import { formatDate, formatNumber, getTickTimestamp, getUniqueTickId, turnDateToSimpleTime } from '../../services/utils';
// Action imports:
import { setCheckedAdditionalTickIds, setSelectedTick } from '../../redux-store/actions/dashboardActions';
// Type imports:
import { Book, BySymbolByTradingDay, isWithBookObject, Requested, TickData, WithBook } from '../../types/api/Types';
import { RootState } from '../../redux-store/reducers';
import { timestampObjectCompareFunction } from '../../services/sortUtils';
import { AlertTickData } from '../../types/api/AlertTypes';
import { NewsTickData } from '../../types/api/NewsTypes';

import { AlertDataIdToCauseMap, TickUniqueIdToAlertMap } from './SigtiRange';
import { PrivateTradeTickData } from '../../types/api/PrivateTypes';

interface IOwnProps {
    securityType: string | null,
    sortedTickData: Array<TickData | WithBook<TickData>>,
    newsDoubleMap: BySymbolByTradingDay<Requested<NewsTickData[]>>,
    isSymbolByLevel: boolean,
    alertDataIdToCauseMap: AlertDataIdToCauseMap
    tickUniqueIdToAlertMap: TickUniqueIdToAlertMap
};
type ReduxProps = ConnectedProps<typeof connector>;
type Props = IOwnProps & ReduxProps;

const TickDataRange: React.FC<Props> = ({
    securityType,
    sortedTickData,
    selectedTick,
    setSelectedTick,
    checkedAdditionalTickIds,
    setCheckedAdditionalTickIds,
    newsDoubleMap,
    isSymbolByLevel,
    alertDataIdToCauseMap,
    tickUniqueIdToAlertMap
}) => {
    const [ showBook, setShowBook ] = useState(true);

    // Used to set the background color of rows that have same timestamp
    const [selectedTimestamp, setSelectedTimestamp] = useState("");

    const [checkedAdditionalTimestamps, setCheckedAdditionalTimestamps] = useState<string[]>([]);

    const tableScrollerRef = useRef<HTMLDivElement>(null!);

    const getCodeTd = (tick: TickData) => {
        switch (tick.tickdata_type) {
            case 'Order':
            case 'Order By Level':
            case 'PrivateOrder':
            case 'PrivateTrade':
            case 'PrivateTradeReport':
                return (
                    <td>
                        {tick.update_code}
                    </td>
                );
            case 'Trade':
                return (
                    <td>
                        {(tick.update_code === 'standard') // if
                        ? 'Standard'
                        : (tick.update_code === 'non_standard') // else if
                        ? 'Non-Standard'
                        : tick.update_code
                        }
                    </td>
                )
            case 'Alert':
                return (
                    <td colSpan={9}>
                        {tick.description}
                    </td>
                );
            case 'Bookmark':
                return (
                    <td colSpan={9}>
                        {tick.bmcomment}
                    </td>
                );
            case 'News':
                return (
                    <td colSpan={9}>
                        {tick.title}
                    </td>
                );
            default:
                return null;
        }
    }

    const getCode = (tick: TickData) => {
        switch (tick.tickdata_type) {
            case 'Order':
            case 'Order By Level':
            case 'PrivateOrder':
            case 'PrivateTrade':
            case 'PrivateTradeReport':
                return tick.update_code;
            case 'Trade':
                return (tick.update_code === 'standard') // if
                    ? 'Standard'
                    : (tick.update_code === 'non_standard') // else if
                    ? 'Non-Standard'
                    : tick.update_code;
            case 'Alert':
                return tick.description;
            case 'Bookmark':
                return tick.bmcomment;
            case 'News':
                return tick.title;
            default:
                return null;
        }
    }

    const getSideTdHelper = (tick: any) => {
        return (
            (tick.side === "bid") // if
                ? <> <FontAwesomeIcon icon={faCircle} color="#01B1A7" size='xs' /> Bid </> 
                : (tick.side === "ask") // else if
                ? <> <FontAwesomeIcon icon={faCircle} color="#EA5F5F" size='xs' /> Ask </>
                : (tick.side === "buy") // else if
                ? <> Buy </>
                : (tick.side === "sell") // else if
                ? <> Sell </>
                : null       
        )
    }
    const getSideTd = (tick: TickData) => {
        switch (tick.tickdata_type) {
            case 'Order':
            case 'Order By Level':
            case 'PrivateOrder':
            case 'PrivateTradeReport':
            case 'PrivateTrade':
                return <td>
                    {getSideTdHelper(tick)}
                    </td>;
            case 'Trade':
                return <td>
                    {(tick.aggressive_party === 'B') // if
                        ? <i className='fas fa-greater-than' />
                        : (tick.aggressive_party === 'S') // else if
                        ? <i className='fas fa-less-than' />
                        : null
                    }
                </td>;
            default:
                return null;
        }
    }

    const getSide = (tick: TickData) => {
        switch (tick.tickdata_type) {
            case 'Order':
            case 'Order By Level':
            case 'PrivateOrder':
            case 'PrivateTradeReport':
            case 'PrivateTrade':
                return tick.side;
            case 'Trade':
                return (tick.aggressive_party === 'B') // if
                ? 'buy'
                : (tick.aggressive_party === 'S') // else if
                ? 'sell'
                : null
            default:
                return null;
        }
    }

    const getCombinedSide = (tick: TickData) => {
        switch (tick.tickdata_type) {
            case 'Order':
            case 'Order By Level':
            case 'PrivateOrder':
            case 'PrivateTradeReport':
            case 'PrivateTrade':
                return (tick.side === null) // if
                ? null
                : (['bid', 'buy'].includes(tick.side)) // else if
                ? 'Bid / Buy'
                : (['ask', 'sell'].includes(tick.side)) // else if
                ? 'Ask / Sell'
                : null;
            case 'Trade':
                return (tick.aggressive_party === 'B') // if
                ? 'Bid / Buy'
                : (tick.aggressive_party === 'S') // else if
                ? 'Ask / Sell'
                : null
            default:
                return null;
        }
    }

    const getVolumeTd = (tick: TickData) => {
        switch (tick.tickdata_type) {
            case 'Order':
            case 'Order By Level':
            case 'PrivateOrder':
            case 'PrivateTradeReport':
            case 'PrivateTrade':
                return <td>{formatNumber(tick.quantity, 'volume')}</td>;
            case 'Trade':
                return <td>{formatNumber(tick.volume, 'volume')}</td>;
            default:
                return null;
        }
    }

    const getVolume = (tick: TickData) => {
        switch (tick.tickdata_type) {
            case 'Order':
            case 'Order By Level':
            case 'PrivateOrder':
            case 'PrivateTradeReport':
            case 'PrivateTrade':
                return tick.quantity;
            case 'Trade':
                return tick.volume;
            default:
                return null;
        }
    }

    const getPriceTd = (tick: TickData) => {
        switch (tick.tickdata_type) {
            case 'Order':
            case 'Order By Level':
            case 'Trade':
            case 'PrivateOrder':
            case 'PrivateTradeReport':
            case 'PrivateTrade':
                return <td>{formatNumber(tick.price)}</td>;
            default:
                return null;
        }
    }

    const getPrice = (tick: TickData) => {
        switch (tick.tickdata_type) {
            case 'Order':
            case 'Order By Level':
            case 'Trade':
            case 'PrivateOrder':
            case 'PrivateTradeReport':
            case 'PrivateTrade':
                return tick.price;
            default:
                return null;
        }
    }

    const getValueTd = (tick: TickData) => {
        switch (tick.tickdata_type) {
            case 'Order':
            case 'Trade':
            case 'PrivateOrder':
            case 'PrivateTradeReport':
            case 'PrivateTrade':
                return <td>{formatNumber(tick.value, 'volume')}</td>;
            case 'Order By Level':
                return <td></td>;
            default:
                return null;
        }
    }

    const getValue = (tick: TickData) => {
        switch (tick.tickdata_type) {
            case 'Order':
            case 'Trade':
            case 'PrivateOrder':
            case 'PrivateTradeReport':
            case 'PrivateTrade':
                return tick.value;
            case 'Order By Level':
                return null
            default:
                return null;
        }
    }

    const getBuyerTd = (tick: TickData) => {
        switch (tick.tickdata_type) {
            case 'Order':
                return <td>{(tick.side === 'bid') ? tick.member : null}</td>;
            case 'Order By Level':
                return <td></td>;
            case 'PrivateOrder':
                return <td>{(tick.side === 'buy') ? tick.comp_id : null}</td>;
            case 'Trade':
                return <td>{tick.trade_buyer}</td>;
            case 'PrivateTrade':
                return <td>{(tick.side === 'buy') ? tick.comp_id : tick.counterparty}</td>;
            case 'PrivateTradeReport':
                return (
                    <td>
                        {(tick.side === 'buy')
                            ? (tick.executing_firm ?? tick.trader_id)
                            : tick.counterparty
                        }
                    </td>
                );
            default:
                return null;
        }
    }

    const getBuyer = (tick: TickData) => {
        switch (tick.tickdata_type) {
            case 'Order':
                return (tick.side === 'bid') ? tick.member : null;
            case 'PrivateOrder':
                return (tick.side === 'buy') ? tick.comp_id : null;
            case 'Trade':
                return tick.trade_buyer;
            case 'PrivateTrade':
                return (tick.side === 'buy') ? tick.comp_id : tick.counterparty;
            case 'PrivateTradeReport':
                return (tick.side === 'buy')
                    ? (tick.executing_firm ?? tick.trader_id)
                    : tick.counterparty;
            default:
                return null;
        }
    }

    const getSellerTd = (tick: TickData) => {
        switch (tick.tickdata_type) {
            case 'Order':
                return <td>{(tick.side === 'ask') ? tick.member : null}</td>
            case 'Order By Level':
                return <td></td>;
            case 'PrivateOrder':
                return <td>{(tick.side === 'sell') ? tick.comp_id : null}</td>
            case 'Trade':
                return <td>{tick.trade_seller}</td>;
            case 'PrivateTrade':
                return <td>{(tick.side === 'sell') ? tick.comp_id : tick.counterparty}</td>
            case 'PrivateTradeReport':
                return (
                    <td>
                        {(tick.side === 'sell')
                            ? (tick.executing_firm ?? tick.trader_id)
                            : tick.counterparty
                        }
                    </td>
                );
            default:
                return null;
        }
    }

    const getSeller = (tick: TickData) => {
        switch (tick.tickdata_type) {
            case 'Order':
                return (tick.side === 'ask') ? tick.member : null;
            case 'PrivateOrder':
                return (tick.side === 'sell') ? tick.comp_id : null;
            case 'Trade':
                return tick.trade_seller;
            case 'PrivateTrade':
                return (tick.side === 'sell') ? tick.comp_id : tick.counterparty
            case 'PrivateTradeReport':
                return (tick.side === 'sell')
                    ? (tick.executing_firm ?? tick.trader_id)
                    : tick.counterparty;
            default:
                return null;
        }
    }

    const getTBuyTd = (tick: TickData) => {
        switch (tick.tickdata_type) {
            case 'PrivateTradeReport':
                return (
                    <td>
                        {tick.side === 'buy' ? tick.trader_id : null}
                    </td>
                );
            case 'Order':
            case 'Order By Level':
            case 'Trade':
            case 'PrivateOrder':
            case 'PrivateTrade':
                return <td></td>;
            default:
                return null;
        }
    }

    const getTBuy = (tick: TickData) => {
        switch (tick.tickdata_type) {
            case 'PrivateTradeReport':
                return tick.side === 'buy' ? tick.trader_id : null;
            default:
                return null;
        }
    }

    const getTSellTd = (tick: TickData) => {
        switch (tick.tickdata_type) {
            case 'PrivateTradeReport':
                return (
                    <td>
                        {tick.side === 'sell' ? tick.trader_id : null}
                    </td>
                );
            case 'Order':
            case 'Order By Level':
            case 'Trade':
            case 'PrivateOrder':
            case 'PrivateTrade':
                return <td></td>;
            default:
                return null;
        }
    }

    const getTSell = (tick: TickData) => {
        switch (tick.tickdata_type) {
            case 'PrivateTradeReport':
                return tick.side === 'sell' ? tick.trader_id : null;
            default:
                return null;
        }
    }

    const getNewsLink = (alert: AlertTickData) => {
        const { symbol, newstimestamp, newstitle } = alert;
        if (
            symbol === null
            || newstimestamp === null
            || newstitle === null
        ) return newstitle;

        const newsDate = turnDateToSimpleTime(new Date(newstimestamp));
        const newsArray = newsDoubleMap?.[symbol]?.[newsDate];

        if (
            newsArray === undefined
            || newsArray === 'waiting'
            || newsArray instanceof Error
        ) return newstitle;

        const matchingNewsItem = Array.isArray(newsArray) 
            ? newsArray.find(newsItem => (newsItem.title === newstitle)) 
            : undefined;

        if (matchingNewsItem === undefined) return newstitle;

        return (
            <a target='_blank' rel='noopener noreferrer' href={matchingNewsItem.link}>
                {matchingNewsItem.title}
            </a>
        );
    }

    const displayRow = (rowTitle: string, rowData: any, key: number) => {
        if (rowData === null || rowData === undefined) return null;
        return (
            <tr key={key}>
                <th>{rowTitle}</th>
                <td>{rowData}</td>
            </tr>
        )
    }

    // ------------------ Display Private Trade Client IDs ------------------
    const displayPrivateTradeClientIds = (tick: PrivateTradeTickData) => {
        return Array.from(
                new Set(
                    (tick.orders || [])
                        .map(order => (order.record_keeping ?? {}).client_id || '')
                        .filter(client_id => client_id) // This removes any empty strings
                )
            ).join(', ')
    }

    // ------------------ Display Private Trade Order IDs ------------------
    const displayPrivateTradeOrderIds = (tick: PrivateTradeTickData) => {
        return Array.from(
                new Set(
                    (tick.orders || [])
                        .map(order => order.id || '')
                        .filter(id => id) // This removes any empty strings
                )
            ).join(', ')
    }

    const handleCheckboxChange = (tickId: string, checked: boolean) => {
        if (!tickId) return;
        if (checked) {
            setCheckedAdditionalTickIds([...checkedAdditionalTickIds, tickId]);
        } else {
            setCheckedAdditionalTickIds(checkedAdditionalTickIds.filter(id => id !== tickId));
        }
    };

    const getPopover = (tick: TickData) => {
        let rowEntries: Array<{title: any; data: any;}> = [];
        switch(tick.tickdata_type) {
            case 'Alert': {
                const alert = tick;
                rowEntries = [{
                    title: 'Alert',
                    data: alert.description
                }, {
                    title: 'Description',
                    data: alert.ad_description
                }, {
                    title: 'Order ID',
                    data: alert.order_id
                }, {
                    title: 'Trade ID',
                    data: alert.trade_id
                }, {
                    title: 'Trader',
                    data: alert.trader_id
                }, {
                    title: 'Price change',
                    data: alert.pricechange
                }, {
                    title: 'Currency',
                    data: alert.currency
                }, {
                    title: 'Value',
                    data: (alert.order_value === undefined || alert.order_value === null) ? undefined : formatNumber(alert.order_value)
                }, {
                    title: 'Trade class',
                    data: alert.trade_class
                }, {
                    title: 'News title',
                    data: getNewsLink(alert)
                }, {
                    title: 'News time',
                    data: (alert.newstimestamp === undefined || alert.newstimestamp === null) ? undefined : formatDate(alert.newstimestamp)
                }, {
                    title: 'Status',
                    data: alert.status
                }];
                break;
            }
            case 'Bookmark': {
                rowEntries=[{
                    title: 'Bookmark',
                    data: tick.bmcomment,
                }, {
                    title: 'User',
                    data: tick.username
                }, {
                    title: 'ID',
                    data: tick.id
                }, {
                    title: 'Timestamp',
                    data: formatDate(tick.timestamp, false, true)
                }];
                break;
            }
            case 'News': {
                return (
                    <OverlayTrigger
                        rootClose
                        placement='left'
                        trigger={['click', 'focus']}
                        overlay={
                            <Popover id='NewsPopover'>
                                <Popover.Header>
                                    {tick.tickdata_type}
                                </Popover.Header>
                                <Popover.Body>
                                    <a href={tick.link} target='_blank' rel='noopener noreferrer'>
                                        {tick.title}
                                    </a>
                                </Popover.Body>
                            </Popover>
                        }
                    >
                        <span className='fas fa-info-circle'/>
                    </OverlayTrigger>
                );
            }
            case 'Order': {
                rowEntries = [{
                    title: 'Trade date',
                    data: formatDate(tick.trade_date, false, true)
                }, {
                    title: 'Order id',
                    data: tick.order_id
                }];

                if (securityType === 'bond') {
                    rowEntries.push(...[{
                        title: 'Yield',
                        data: tick.yield
                    }, {
                        title: 'Duration',
                        data: tick.duration
                    }, {
                        title: 'Dirty price',
                        data: tick.dirty_price
                    }, {
                        title: 'Price per point',
                        data: tick.price_per_point
                    }]);
                }
                break;
            }
            case 'Order By Level': {
                rowEntries = [{
                    title: 'Trade date',
                    data: formatDate(tick.trade_date, false, true)
                }, {
                    title: 'Order id',
                    data: tick.id
                }, {
                    title: '# of orders',
                    data: tick.number_of_orders
                }, {
                    title: 'Level',
                    data: tick.level
                }];

                if (securityType === 'bond') {
                    rowEntries.push(...[{
                        title: 'Yield',
                        data: tick.yield
                    }, {
                        title: 'Duration',
                        data: tick.duration
                    }, {
                        title: 'Dirty price',
                        data: tick.dirty_price
                    }, {
                        title: 'Price per point',
                        data: tick.price_per_point
                    }]);
                }
                break;
            }
            case 'PrivateOrder': {
                rowEntries = [{
                    title: 'Order Type',
                    data: tick.type
                }, {
                    title: 'Time in force',
                    data: tick.time_in_force
                }, {
                    title: 'Order ID',
                    data: tick.market_feed_id
                }, {
                    title: 'Remaining Quantity',
                    data: formatNumber(tick.remaining_quantity, 'volume')
                }, {
                    title: 'Trader ID',
                    data: tick.trader_id
                }, {
                    title: 'Minimum Quantity',
                    data: formatNumber(tick.minimum_quantity, 'volume')
                }, {
                    title: 'Visible Quantity',
                    data: formatNumber(tick.visible_quantity, 'volume')
                }, {
                    title: 'Peg Type',
                    data: tick.peg_type
                }, {
                    title: 'Peg Difference',
                    data: tick.peg_difference
                }, {
                    title: 'Cancel Reason',
                    data: tick.cancel_reason
                }, {
                    title: 'Reject Reason',
                    data: tick.action_reject_reason
                }, {
                    title: 'Client ID',
                    data: tick.record_keeping.client_id !== 'none' ? tick.record_keeping.client_id : "-"
                }, {
                    title: 'Client Type',
                    data: tick.record_keeping.client_type !== 'none' ? tick.record_keeping.client_type : "-"
                }, {
                    title: 'Executing Trader ID',
                    data: tick.record_keeping.executing_trader_id !== 'none' ? tick.record_keeping.executing_trader_id : "-"
                }, {
                    title: 'Executing Trader Type',
                    data: tick.record_keeping.executing_trader_type !== 'none' ? tick.record_keeping.executing_trader_type : "-"
                }, {
                    title: 'Investment Decision ID',
                    data: tick.record_keeping.investment_decision_id !== 'none' ? tick.record_keeping.investment_decision_id : "-"
                }, {
                    title: 'Investment Decision Type',
                    data: tick.record_keeping.investment_decision_type !== 'none' ? tick.record_keeping.investment_decision_type : "-"
                }, {
                    title: 'Liquidity Provision',
                    data: tick.liquidity_provision ? 'True' : 'False'
                }]
                break;
            }
            case 'PrivateTrade': {
                rowEntries = [{
                    title: 'Trade ID',
                    data: tick.trade_id
                },{
                    title: 'Order IDs',
                    data: displayPrivateTradeOrderIds(tick)
                }, {
                    title: 'Client IDs',
                    data: displayPrivateTradeClientIds(tick)
                },  {
                    title: 'Cancelled',
                    data: tick.cancelled
                }, {
                    title: 'Cancelled Time',
                    data: tick.cancelled_at
                }, {
                    title: 'Replaced Time',
                    data: tick.replaced_at
                }];
                break;
            }
            case 'PrivateTradeReport': {
                rowEntries = [{
                    title: 'Status',
                    data: tick.status
                }, {
                    title: 'Registration Time',
                    data: formatDate(tick.trade_registration_timestamp, false, true)
                }, {
                    title: 'Trader ID',
                    data: tick.trader_id
                }, {
                    title: 'Executing Firm',
                    data: tick.executing_firm
                }, {
                    title: 'Trade Type',
                    data: tick.trade_type
                }, {
                    title: 'Capacity',
                    data: tick.capacity
                }, {
                    title: 'Internal',
                    data: `${tick.is_internal}` // Bool to string cast
                }, {
                    title: 'Is Initiated',
                    data: `${tick.is_initiated}` // Bool to string cast
                }];
                break;
            }
            case 'Trade': {
                rowEntries = [{
                    title: 'Trade Date',
                    data: formatDate(tick.trade_date, false, true)
                }, {
                    title: 'Trade Class',
                    data: tick.trade_class
                }, {
                    title: 'Trade Number',
                    data: tick.trade_number
                }, {
                    title: 'Trade ID',
                    data: tick.trade_id
                }, {
                    title: 'Trade Cancel',
                    data: tick.trade_cancel
                }, {
                    title: 'Updates Last Paid',
                    data: tick.trade_updates_last_paid
                }];
                break;
            }
            default:
                return null;
        }
        return (
            <OverlayTrigger
                rootClose
                placement='left'
                trigger={['focus', 'click']}
                overlay={
                    <Popover id='AlertPopover' className='max-width-500'>
                        <Popover.Header>
                            {tick.tickdata_type}
                        </Popover.Header>
                        <Popover.Body>
                            <Table className='SimpleCompressedTable'>
                                <tbody>
                                    {rowEntries.map(({ title, data }, index) => displayRow(title, data, index))}
                                </tbody>
                            </Table>
                        </Popover.Body>
                    </Popover>
                }
            >
                <span
                    className='fas fa-info-circle'
                    onClick={e => e.stopPropagation()}
                />
            </OverlayTrigger>
        );
    }

    const displayTable = () => {
        // const isOrder = (tick: TickData | WithBook<TickData>): tick is WithBook<OrderTickData> => (tick.tickdata_type === 'Insert' || tick.tickdata_type === 'Update' || tick.tickdata_type === 'Deleted');
        let recentBookData: Book = {
            bookAskPrice: null,
            bookAskQuantity: null,
            bookBidPrice: null,
            bookBidQuantity: null
        }
        const withBookTickData: Array<WithBook<TickData>> = [];
        for (let tick of sortedTickData){
            if (isWithBookObject(tick)) {
                recentBookData = tick.book
                withBookTickData.push({
                    tick: tick.tick,
                    book: recentBookData
                });
            } else {
                const tickWithBook: WithBook<TickData> = {
                    tick,
                    book: recentBookData
                };
                withBookTickData.push(tickWithBook);
            }
        }

        const bookColumns: IColumn<WithBook<TickData>>[] = (showBook) ? [{
            title: 'Bid Q',
            dataAccessor: ({ book }) => formatNumber(book.bookBidQuantity, 'volume'),
            simpleSortable: ({ book }) => book.bookBidQuantity,
            sortTiebreaker: (a, b) => timestampObjectCompareFunction(a.tick, b.tick),
            filter: {
               id: 'Bid Q',
               type: 'string',
               stringValue: ({book}) => book.bookBidQuantity?.toString() ?? null
            }
        }, {
            title: 'Bid P',
            dataAccessor: ({ book }) => formatNumber(book.bookBidPrice),
            simpleSortable: ({ book }) => book.bookBidPrice,
            sortTiebreaker: (a, b) => timestampObjectCompareFunction(a.tick, b.tick),
            filter: {
               id: 'Bid P',
               type: 'string',
               stringValue: ({book}) => book.bookBidPrice?.toString() ?? null
            }
        }, {
            title: 'Ask P',
            dataAccessor: ({ book }) => formatNumber(book.bookAskPrice),
            simpleSortable: ({ book }) => book.bookAskPrice,
            sortTiebreaker: (a, b) => timestampObjectCompareFunction(a.tick, b.tick),
            filter: {
               id: 'Ask P',
               type: 'string',
               stringValue: ({book}) => book.bookAskPrice?.toString() ?? null
            }
        }, {
            title: 'Ask Q',
            dataAccessor: ({ book }) => formatNumber(book.bookAskQuantity, 'volume'),
            simpleSortable: ({ book }) => book.bookAskQuantity,
            sortTiebreaker: (a, b) => timestampObjectCompareFunction(a.tick, b.tick),
            filter: {
               id: 'Ask Q',
               type: 'string',
               stringValue: ({book}) => book.bookAskQuantity?.toString() ?? null
            }
        }] : [];

        const arrowColumn: IColumn<WithBook<TickData>> = {
            title: (showBook) ? (
                <FontAwesomeIcon
                    icon={faChevronDown}
                    className='BookToggleArrow'
                    onClick={() => setShowBook(false)}
                />
            ) : (
                <FontAwesomeIcon
                    icon={faChevronRight}
                    className='BookToggleArrow'
                    onClick={() => setShowBook(true)}
                />
            ),
            dataAccessor: () => null
        }

        // ============================ RENDER FUNCTIONS ============================
        // ------------------ Render Alert Body ------------------
        const RenderAlertBody = ({ cause }: { cause: TickData }) => {
            return (
                <>
                    Alert
                    <Button
                        variant='outline-dark'
                        className='VerySmallButton'
                        title='Go to Cause'
                        // @ts-ignore
                        onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            setSelectedTick(cause);
                        }}
                    >
                        <FontAwesomeIcon
                            icon={faBell} color="#FFB31F"
                        />
                    </Button>
                </>
            )
        }
        // ------------------ Render Alert Button ------------------
        const RenderAlertButton = ({ alert, tick }: { alert: AlertTickData, tick: TickData }) => {
            return (
                <>
                    {tick.tickdata_type}
                    <Button
                        variant='outline-dark'
                        className='VerySmallButton'
                        title='Go to Alert'
                        // @ts-ignore
                        onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            setSelectedTick(alert);
                        }}
                    >
                        <FontAwesomeIcon
                            icon={faBell}
                        />
                    </Button>
                </>
            )
        }

        return (
            <StickyHeaderTable hideScrollbar={false} setRef={tableScrollerRef}>
                <SortFilterTable
                    data={withBookTickData}
                    isLoading={false}
                    isError={false}
                    onRowClick={({tick}) => {
                        setSelectedTimestamp(getTickTimestamp(tick) || "");
                        setSelectedTick(tick)
                    }}
                    rowId={({tick}) => getUniqueTickId(tick) ?? undefined}
                    rowClassName={({tick}) => (tick === selectedTick) ? 'current' : undefined}
                    columns={[{
                        title: (
                            <div className="CheckboxHeader">
                                {checkedAdditionalTickIds.length > 0 ? (
                                    <input
                                        type="checkbox"
                                        checked={true}
                                        onChange={e => {
                                            if (!e.target.checked) {
                                                setCheckedAdditionalTickIds([]);
                                            }
                                        }}
                                        id="MasterCheckbox"
                                        name="MasterCheckbox"
                                        title="Uncheck to clear all checked rows"
                                    />
                                ) : null}
                            </div>
                        ),
                        dataAccessor: ({ tick }) => {
                            const tickId = getUniqueTickId(tick);
                            return (
                                <input
                                    type="checkbox"
                                    checked={tickId ? checkedAdditionalTickIds.includes(tickId) : false}
                                    onChange={(e) => handleCheckboxChange(tickId || '', e.target.checked)}
                                    onClick={(e) => e.stopPropagation()}
                                />
                            )
                        },
                        textAlign: 'center',
                    }, {
                        title: 'Timestamp',
                        dataAccessor: ({ tick }) => formatDate(tick.timestamp, true, true),
                        // set background color of colum that have same timestamp
                        isColorTimestamp: ({ tick }) => getTickTimestamp(tick) === selectedTimestamp,
                        textAlign: 'left',
                        sortable: (a, b) => timestampObjectCompareFunction(a.tick, b.tick),
                        filter: {
                           id: 'Timestamp',
                           type: 'string',
                           stringValue: ({tick}) => tick.timestamp
                        }
                    }, {
                        title: 'Type',
                        dataAccessor: ({ tick }) => {
                            if (tick.tickdata_type === 'Alert') {
                                if (!tick.ad_id) return 'Alert';
                                const cause = alertDataIdToCauseMap[tick.ad_id];
                                if (!cause) return "Alert";
                                return (
                                    <RenderAlertBody cause={cause} />
                                )
                            } else {
                                const uniqueTickId = getUniqueTickId(tick);
                                if (uniqueTickId === null) return tick.tickdata_type;
                                const alert = tickUniqueIdToAlertMap[uniqueTickId];
                                if (alert === undefined) return tick.tickdata_type;
                                if (alert.id === 3) {
                                    if (tick.tickdata_type !== 'PrivateOrder' || (tick as any).update_code !== 'New') {
                                        return tick.tickdata_type;
                                    }
                                }
                                return (
                                    <RenderAlertButton alert={alert} tick={tick} />
                                );
                            }
                        },
                        textAlign: 'left',
                        simpleSortable: ({ tick }) => tick.tickdata_type,
                        sortTiebreaker: (a, b) => timestampObjectCompareFunction(a.tick, b.tick),
                        filter: {
                           id: 'Type',
                           type: [(isSymbolByLevel) ? 'Order By Level' : 'Order', 'Trade', 'Alert', 'News', 'Bookmark', 'PrivateOrder', 'PrivateTrade', 'PrivateTradeReport'],
                           multiple: true, // Enable multi-select filtering
                           stringValue: ({tick}) => tick.tickdata_type
                        }
                    }, {
                        title: 'Code',
                        dataAccessor: ({ tick }) => getCodeTd(tick),
                        overrideTd: true,
                        textAlign: 'left',
                        simpleSortable: ({ tick }) => getCode(tick),
                        sortTiebreaker: (a, b) => timestampObjectCompareFunction(a.tick, b.tick),
                        filter: {
                            id: 'Code',
                            type: ['Insert', 'Update', 'Deleted', 'Standard', 'Non-Standard', 'Execution', 'New', 'Filled', 'PartiallyFilled', 'Cancelled', 'PendingCancel', 'Replaced', 'Rejected', 'TRReceivedU', 'TRSentU', 'TRReceivedB', 'TRSentB', 'TRReject', 'TRCancel', 'TRBroken', 'TRMatched'],
                            multiple: true, // Enable multi-select filtering
                            stringValue: ({ tick }) => getCode(tick)
                          }
                    }, {
                        title: 'Side',
                        dataAccessor: ({ tick }) => getSideTd(tick),
                        overrideTd: true,
                        textAlign: 'left',
                        simpleSortable: ({ tick }) => getSide(tick),
                        sortTiebreaker: (a, b) => timestampObjectCompareFunction(a.tick, b.tick),
                        filter: {
                           id: 'Side',
                           type: ['Bid / Buy', 'Ask / Sell'],
                           multiple: true, // Enable multi-select filtering
                           stringValue: ({tick}) => getCombinedSide(tick)
                        }
                    }, {
                        title: 'Volume',
                        dataAccessor: ({ tick }) => getVolumeTd(tick),
                        overrideTd: true,
                        simpleSortable: ({ tick }) => getVolume(tick),
                        sortTiebreaker: (a, b) => timestampObjectCompareFunction(a.tick, b.tick),
                        filter: {
                           id: 'Volume',
                           type: 'string',
                           stringValue: ({tick}) => getVolume(tick)?.toString() ?? null
                        }
                    }, {
                        title: 'Price',
                        dataAccessor: ({ tick }) => getPriceTd(tick),
                        overrideTd: true,
                        simpleSortable: ({ tick }) => getPrice(tick),
                        sortTiebreaker: (a, b) => timestampObjectCompareFunction(a.tick, b.tick),
                        filter: {
                           id: 'Price',
                           type: 'string',
                           stringValue: ({tick}) => {
                              const price = getPrice(tick);
                              return price !== null && price !== undefined ? price.toString().replace('.', ',') : null;
                           }
                        }
                    }, {
                        title: 'Value',
                        dataAccessor: ({ tick }) => getValueTd(tick),
                        overrideTd: true,
                        simpleSortable: ({ tick }) => getValue(tick),
                        sortTiebreaker: (a, b) => timestampObjectCompareFunction(a.tick, b.tick),
                        filter: {
                            id: 'Value',
                            type: 'string',
                            stringValue: ({tick}) => getValue(tick)?.toString() ?? null
                        }
                    }, {
                        title: 'Buyer',
                        dataAccessor: ({ tick }) => getBuyerTd(tick),
                        overrideTd: true,
                        textAlign: 'left',
                        simpleSortable: ({ tick }) => getBuyer(tick),
                        sortTiebreaker: (a, b) => timestampObjectCompareFunction(a.tick, b.tick),
                        filter: {
                           id: 'Buyer',
                           type: 'string',
                           stringValue: ({tick}) => getBuyer(tick)
                        }
                    }, {
                        title: 'Seller',
                        dataAccessor: ({ tick }) => getSellerTd(tick),
                        overrideTd: true,
                        textAlign: 'left',
                        simpleSortable: ({ tick }) => getSeller(tick),
                        sortTiebreaker: (a, b) => timestampObjectCompareFunction(a.tick, b.tick),
                        filter: {
                           id: 'Seller',
                           type: 'string',
                           stringValue: ({tick}) => getSeller(tick)
                        }
                    }, {
                        title: 'TBuy',
                        dataAccessor: ({ tick }) => getTBuyTd(tick),
                        overrideTd: true,
                        textAlign: 'left',
                        simpleSortable: ({ tick }) => getTBuy(tick),
                        sortTiebreaker: (a, b) => timestampObjectCompareFunction(a.tick, b.tick),
                        filter: {
                           id: 'TBuy',
                           type: 'string',
                           stringValue: ({tick}) => getTBuy(tick)
                        }
                    }, {
                        title: 'TSell',
                        dataAccessor: ({ tick }) => getTSellTd(tick),
                        overrideTd: true,
                        textAlign: 'left',
                        simpleSortable: ({ tick }) => getTSell(tick),
                        sortTiebreaker: (a, b) => timestampObjectCompareFunction(a.tick, b.tick),
                        filter: {
                           id: 'TSell',
                           type: 'string',
                           stringValue: ({tick}) => getTSell(tick)
                        }
                    }, {
                        title: 'Private Client ID',
                        textAlign: 'left',
                        cellClassName: 'PrivateOrderClientID',
                        dataAccessor: ({ tick } ) => {
                            if (tick.tickdata_type === 'PrivateOrder') {
                                return tick.record_keeping.client_id;
                            } else {
                                return null;
                            }
                        }
                    }, 
                    // the extra info button
                    {
                        title: '',
                        dataAccessor: ({ tick }) => getPopover(tick),
                        textAlign: 'center'
                    }, {
                        title: '',
                        dataAccessor: () => null,
                        cellClassName: 'TickBookBorder'
                    },
                    arrowColumn,
                    ...bookColumns
                    ]}
                />
            </StickyHeaderTable>
        );
    }

    const exportToExcel = async () => {
        const table = document.querySelector('#TickDataRange table');
        const ws = XLSX.utils.table_to_sheet(table);
        let data: any[] = XLSX.utils.sheet_to_json(ws, {header:1});
    
        // Replace unwanted headers
        data = data.map((row: any) => row.map((header: any) => header === 'TypeAllOrderTradeAlertNewsBookmarkPrivateOrderPrivateTradePrivateTradeReport' ? 'Type' : header));
        data = data.map((row: any) => row.map((header: any) => header === 'CodeAllInsertUpdateDeletedStandardNon-StandardExecutionNewFilledPartiallyFilledCancelledPendingCancelReplacedRejectedTRReceivedU' ? 'Code' : header));
        data = data.map((row: any) => row.map((header: any) => header === 'SideAllBid / BuyAsk / Sell' ? 'Side' : header));
    
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Tick Data');
    
        // Add data to worksheet
        data.forEach((row: any[], rowIndex) => {
            row.forEach((value: any, colIndex) => {
                const cell = worksheet.getCell(rowIndex + 1, colIndex + 1);
                cell.value = value;
    
                // Make headers bold
                if (rowIndex === 0) {
                    cell.font = { bold: true };
                }
            });
        });
    
        // Adjust column widths
        worksheet.columns.forEach(column => {
            let maxColumnLength = 0;
            if (column && column.eachCell) {
                column.eachCell({ includeEmpty: true }, cell => {
                    const columnLength = cell.text.length;
                    if (columnLength > maxColumnLength) {
                        maxColumnLength = columnLength;
                    }
                });
            }
            column.width = maxColumnLength < 10 ? 10 : maxColumnLength;
        });
    
        // Write to file
        workbook.xlsx.writeBuffer().then((buffer) => {
            const blob = new Blob([buffer], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'});
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `tick_data.xlsx`;
            a.click();
        });
    }

    return (
        <div id='TickDataRange'>
            <div id='TickDataHead'>
                <h5 className='SectionTitle'>Tick Data</h5>
                <div id='buttonDiv'>
                    <button id='excelButton' onClick={exportToExcel} className='VerySmallButton btn btn-dark btn-sm'>Excel</button>
                </div>
            </div>
            <div className='TickDataPanel'>
                <div className='TickDataTableWrapper'>
                    {displayTable()}
                </div>
            </div>
        </div>
    );
}

function mapStateToProps({ dashboardReducer }: RootState) {
    const { selectedTick, checkedAdditionalTickIds } = dashboardReducer;
    return {
        selectedTick,
        checkedAdditionalTickIds
    };
}

const connector = connect(mapStateToProps, { setSelectedTick, setCheckedAdditionalTickIds });
export default connector(TickDataRange);
